/**
 * Today model — V98-3-3 / V98-3-4 (PRD G-2, G-3 multi-hat, §6; ADR-0046 §2;
 * research §2 items 1–4, 14–15, 23). Pure: no React, no fetch.
 *
 * Composition comes from GET /today alone — there are no role pages and no
 * role switcher. The viewer's hats are the union of their assignments; the
 * page shows the sections for the hats they hold and ONE urgency-sorted
 * Owed list (the server's order) with a hat tag per row, each item once.
 *
 * Presentation rules carried from cert-program-ux rounds 3–5:
 *   - one hat tag per row; no numbered badges, no "as <hat>" subtitles;
 *   - hat filter chips only when > 8 items AND > 1 distinct tag;
 *   - every count carries a scope label (Org / Yours / a program / Shared);
 *   - red appears once per item, on its highest tier (Owed → Open by owner
 *     → Next 14 days); lower tiers point to Owed instead of repeating red;
 *   - the approver queue: overdue first, then what blocks a filing, then due;
 *   - a compensating review is never the primary action.
 */
import type { OwedRow, ProgramCard, TodayAction, TodayView, WaitingRow, WillMiss } from '@aigovops/core';
import type { BlockerRow } from './coverage-api';
import { programTitle } from './nav-config';

export type Names = ReadonlyMap<string, string>;
export type Tone = 'red' | 'amber' | 'neutral';
export type VerdictStatus = 'on_track' | 'at_risk' | 'off_track';

const DAY = 86_400_000;

export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
export const nameOf = (names: Names, id: string | null | undefined, fallback = 'Someone'): string =>
  (id && names.get(id)) || fallback;

/** UTC calendar day of an ISO instant or date. */
export const day = (iso: string): string => iso.slice(0, 10);
export const daysBetween = (fromDay: string, toDay: string): number =>
  Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY);

export function fmtDay(iso: string | null | undefined, withYear = false): string {
  if (!iso) return '';
  const d = new Date(`${day(iso)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
}

/** The row's effective date: the earlier of due and hard date. */
export const whenOf = (r: { due_at: string | null; hard_date: string | null }): string | null =>
  [r.due_at, r.hard_date].filter(Boolean).map((d) => day(d!)).sort()[0] ?? null;

// ---- hats ---------------------------------------------------------------------------

/** The one tag shown on an Owed row. */
export const HAT_TAG: Record<string, string> = {
  approver: 'Approver',
  backup_approver: 'Backup approver',
  owner: 'Owner',
  system_owner: 'System owner',
  program_lead: 'Program lead',
  legal_reviewer: 'Legal',
  attester: 'Attester',
  access_reviewer: 'Access reviewer',
  checker: 'Independent check',
  escalation: 'Escalated to you',
  preparer: 'Author',
  ask: 'Asked of you',
  alternate_signer: 'Alternate signer',
  workspace_admin: 'Admin',
  independent_party: 'Independent party',
};

export function hatTag(row: Pick<OwedRow, 'hat' | 'on_behalf_of_user_id'>, names: Names): string {
  if (row.on_behalf_of_user_id) return `For ${firstName(nameOf(names, row.on_behalf_of_user_id, 'a colleague'))}`;
  return HAT_TAG[row.hat] ?? row.hat.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** The row's action verb (visible label; the accessible name appends the title — label-in-name). */
export const ACTION_VERB: Record<TodayAction, string> = {
  answer_ask: 'Answer',
  answer_fact: 'Answer Legal',
  decide: 'Decide',
  sign: 'Review & sign',
  approve_rows: 'Review & approve',
  review_rows: 'Review',
  conclude_check: 'Do the check',
  handle_escalation: 'Open',
  resolve_dispute: 'Resolve',
  add_independent_party: 'Add advisor',
  find_approver: 'See options',
  review_proposal: 'Review proposal',
  revise: 'Revise and resubmit',
  complete_review: 'Complete',
  remediate: 'Fix',
  complete_occurrence: 'Complete',
  complete: 'Open',
};

/** R3-U12: what an item is waiting for, as plain words (a status line never shows the raw action code). */
export const NEXT_ACTION_TEXT: Record<string, string> = {
  answer_ask: 'An ask to answer',
  answer_fact: 'A question from Legal',
  decide: 'An applicability decision',
  sign: 'Waiting for a signature',
  approve_rows: 'Rows to approve',
  review_rows: 'Rows to review',
  conclude_check: 'An independent check to do',
  handle_escalation: 'An escalation to handle',
  resolve_dispute: 'A dispute to resolve',
  add_independent_party: 'Needs an independent party',
  find_approver: 'Needs another approver',
  review_proposal: 'A proposal awaiting Legal',
  revise: 'Sent back · to revise and resubmit',
  complete_review: 'A review to complete',
  remediate: 'To fix',
  complete_occurrence: 'Due to complete',
  complete: 'To complete',
};
export const nextActionText = (action: string): string => NEXT_ACTION_TEXT[action] ?? 'Open';

/**
 * The one action a row offers (V98-3 review U1: no dead ends). Work items
 * carry the server's action contract (`act`, S10): a plain completion acts
 * inline; an action with an owning-record link opens that record; anything
 * else is taken on the item page. Every other row opens the item.
 */
export type RowAct =
  | { kind: 'link'; to: string; label: string }
  | { kind: 'complete'; workItemId: string; label: string };

const COMPLETE_PATH = /^\/work-items\/([^/]+)\/complete$/;

export function rowAct(row: Pick<OwedRow, 'action' | 'ref' | 'act' | 'subtype'>, prefix: string, from = 'today'): RowAct {
  const a = row.act;
  if (a) {
    // Only a plain task completes from the list; evidence needs its reference, so it opens the item.
    const done = a.kind === 'endpoint' && a.path && row.subtype === 'task' ? COMPLETE_PATH.exec(a.path) : null;
    if (done) return { kind: 'complete', workItemId: decodeURIComponent(done[1]!), label: a.label };
    if (a.href) return { kind: 'link', to: `${prefix}${a.href}`, label: a.label };
    return { kind: 'link', to: itemHref(prefix, row.ref, from), label: a.label };
  }
  return { kind: 'link', to: itemHref(prefix, row.ref, from), label: ACTION_VERB[row.action] };
}

/** Program-neutral item link with a return crumb (`from`). */
export function itemHref(prefix: string, ref: { kind: string; id: string }, from = 'today'): string {
  return `${prefix}/items/${encodeURIComponent(ref.kind)}/${encodeURIComponent(ref.id)}?from=${encodeURIComponent(from)}`;
}

// ---- chips --------------------------------------------------------------------------

export interface Chip { text: string; tone: Tone }

/** The date chip for a row: overdue (red), blocked (amber), or when it is due. */
export function dueChip(r: { due_at: string | null; hard_date: string | null; blocked?: boolean; action?: TodayAction }, todayDay: string): Chip {
  const when = whenOf(r);
  if (when && when < todayDay) {
    const n = daysBetween(when, todayDay);
    return { text: `${n} day${n === 1 ? '' : 's'} overdue`, tone: 'red' };
  }
  if (r.blocked) {
    return {
      text: r.action === 'add_independent_party' ? 'Needs an independent party' : r.action === 'find_approver' ? 'Needs another approver' : 'Nobody eligible yet',
      tone: 'amber',
    };
  }
  if (!when) return { text: 'No date', tone: 'neutral' };
  const d = daysBetween(todayDay, when);
  const text = d === 0 ? 'Due today' : d === 1 ? 'Due tomorrow' : d <= 7 ? `Due in ${d} days` : `Due ${fmtDay(when)}`;
  return { text: r.hard_date && day(r.hard_date) === when ? text.replace(/^Due/, 'Hard date') : text, tone: 'neutral' };
}

/** Plain-language SoD state for a row the viewer prepared (shown as a state, never as a button). */
export function sodNote(row: Pick<OwedRow, 'action' | 'hat' | 'blocked'>, solo: boolean): string | null {
  const needsSecond = row.action === 'find_approver' || row.action === 'add_independent_party' || (row.blocked && row.hat === 'access_reviewer');
  // One-approver org (U8): ONE shared state on every such row; the solo card owns the fix.
  if (solo) return needsSecond ? SOLO_NOTE : null;
  if (row.action === 'find_approver') return 'You prepared this · needs another approver';
  if (row.action === 'add_independent_party') return 'Needs someone outside the pair';
  if (row.blocked && row.hat === 'access_reviewer') return 'Reviewed by you · needs another approver';
  return null;
}
export const SOLO_NOTE = 'Needs a second person';

// ---- composition --------------------------------------------------------------------

export type SectionId = 'delegate_banner' | 'solo' | 'to_sign' | 'owed' | 'waiting' | 'legal' | 'programs' | 'owners' | 'next14' | 'at_risk' | 'signed_today';

export interface Composition {
  /** Owner-type hats only, no program, not admin: the one-hat minimal shell. */
  minimal: boolean;
  isLead: boolean;
  /** The Today rows the viewer owes, in server urgency order, each once. */
  owed: OwedRow[];
  /** Approver queue (non-lead approvers get To sign / To provide). */
  toSign: OwedRow[];
  /** The rest of Owed when `toSign` is split out; else all of Owed. */
  owedList: OwedRow[];
  waiting: WaitingRow[];
  /** People the viewer is covering for (delegate). */
  coveringFor: string[];
  hasLegal: boolean;
  isApprover: boolean;
  /** One-approver org state: items the viewer prepared that nobody else can approve. */
  solo: { count: number; keys: string[] } | null;
  showHatFilter: boolean;
  /** The single primary "Next" action (null when another primary owns the layer, e.g. solo). */
  next: OwedRow | null;
  sections: SectionId[];
}

const APPROVER_HATS = ['approver', 'backup_approver', 'alternate_signer'];
const OWNER_TYPE = ['owner', 'system_owner'];

/**
 * Approver queue order (R3-S9): an item projected to miss its hard date
 * (`will_miss` — rule 23) first, then overdue, then items that block a filing
 * (hard date), then the nearest date.
 */
export function approverOrder(rows: readonly OwedRow[], missKeys: ReadonlySet<string> = new Set()): OwedRow[] {
  return [...rows].sort((a, b) => {
    const am = missKeys.has(a.key), bm = missKeys.has(b.key);
    if (am !== bm) return am ? -1 : 1;
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.blocks_filing !== b.blocks_filing) return a.blocks_filing ? -1 : 1;
    const ad = whenOf(a), bd = whenOf(b);
    if (ad !== bd) return ad === null ? 1 : bd === null ? -1 : ad < bd ? -1 : 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

/** Rows the viewer prepared that only another approver could take (one-approver org signal). */
export function selfPreparedBlocked(owed: readonly OwedRow[]): OwedRow[] {
  return owed.filter((r) => r.action === 'find_approver' || r.action === 'add_independent_party' || (r.blocked && r.hat === 'access_reviewer'));
}

export function distinctTags(rows: readonly OwedRow[], names: Names): string[] {
  return [...new Set(rows.map((r) => hatTag(r, names)))];
}

/** The projected miss the band shows: the next hard date that will actually be missed (S9). */
export function willMissOf(view: Pick<TodayView, 'next_hard_date' | 'counts'>): WillMiss | null {
  return view.next_hard_date?.will_miss ?? view.counts?.will_miss ?? null;
}

/** Every projected miss key (the band's will_miss and the rest of the misses list). */
export function missKeysOf(view: Pick<TodayView, 'next_hard_date' | 'counts'>): Set<string> {
  const m = willMissOf(view);
  return new Set([...(m ? [m.key] : []), ...(view.next_hard_date?.misses ?? []).map((x) => x.key)]);
}

/**
 * The single Next primary. R3-S9: overdue ranks before future —
 *   1. an item ALREADY past its hard date (missed; nothing outranks it),
 *   2. the item projected to miss its hard date (U3: above internal overdue),
 *   3. any other overdue item,
 *   4. everything else in the server's urgency order.
 * Within a tier the server's order holds.
 */
export function pickNext(view: Pick<TodayView, 'owed' | 'next_hard_date' | 'counts' | 'generated_at'>): OwedRow | null {
  const today = day(view.generated_at);
  const misses = missKeysOf(view);
  const miss = willMissOf(view);
  const tier = (r: OwedRow) => (r.hard_date && day(r.hard_date) < today ? 0
    : miss && r.key === miss.key ? 1 : misses.has(r.key) ? 2 : r.overdue ? 3 : 4);
  let best: OwedRow | null = null;
  for (const r of view.owed) if (!best || tier(r) < tier(best)) best = r;
  return best;
}

/**
 * `independentParties`: seats filled by an ACTIVE member (S6 — an invite or a
 * bare contact doesn't count). `noOtherApprover`: nobody else could approve
 * the viewer's blocked items, now or later (no other approver, nobody away
 * who comes back) — what separates a one-approver org from an approver who
 * is only out. Unknown (null/undefined) keeps the solo state.
 */
export function composeToday(view: TodayView, opts: { names: Names; independentParties: number | null; noOtherApprover?: boolean | null }): Composition {
  const v = view.viewer;
  const isLead = v.is_admin || v.lead_program_ids.length > 0;
  const minimal = !isLead && v.hats.length > 0 && v.hats.every((h) => OWNER_TYPE.includes(h));
  const owed = view.owed;
  const isApprover = v.hats.some((h) => APPROVER_HATS.includes(h)) || owed.some((r) => r.action === 'sign');
  const signRows = owed.filter((r) => r.action === 'sign' || r.action === 'approve_rows');
  const splitSign = !isLead && !minimal && signRows.length > 0;
  const toSign = splitSign ? approverOrder(signRows, missKeysOf(view)) : [];
  const owedList = splitSign ? owed.filter((r) => !signRows.includes(r)) : owed;
  const blockedMine = selfPreparedBlocked(owed);
  // One approver: things the viewer prepared have no other eligible approver and no independent party exists.
  const solo = blockedMine.length > 0 && opts.independentParties === 0 && opts.noOtherApprover !== false && (isApprover || v.is_admin)
    ? { count: blockedMine.length, keys: blockedMine.map((r) => r.key) } : null;
  const coveringFor = [...new Set(owed.map((r) => r.on_behalf_of_user_id).filter(Boolean) as string[])];
  const hasLegal = v.hats.includes('legal_reviewer');
  const sections: SectionId[] = [];
  if (coveringFor.length) sections.push('delegate_banner');
  if (solo) sections.push('solo');
  if (isLead && view.signatures_at_risk && view.signatures_at_risk.length) sections.push('at_risk');
  if (toSign.length) sections.push('to_sign');
  sections.push('owed');
  if (view.waiting.length) sections.push('waiting');
  if (isApprover) sections.push('signed_today');
  if (hasLegal) sections.push('legal');
  if (!minimal) sections.push('programs');
  if (isLead) sections.push('owners');
  sections.push('next14');
  // The approver queue is itself the action list; solo's advisor seat owns the primary.
  const next = solo || toSign.length ? null : pickNext(view);
  return {
    minimal, isLead, owed, toSign, owedList, waiting: view.waiting, coveringFor, hasLegal, isApprover, solo,
    showHatFilter: owedList.length > 8 && distinctTags(owedList, opts.names).length > 1,
    next, sections,
  };
}

// ---- counts + verdict ---------------------------------------------------------------------

export interface ScopedCount { scope: string; value: number; noun?: string; emphasis?: 'bad'; href?: string }

/** VerdictBand counts, each scoped; the overdue count is the only red. */
export function todayCounts(view: TodayView, cards: readonly ProgramCard[]): ScopedCount[] {
  const c = view.counts;
  const out: ScopedCount[] = [];
  if (c.org) {
    out.push({ scope: 'Org', value: c.org.open, noun: 'open' });
    if (c.org.overdue > 0) out.push({ scope: 'Org', value: c.org.overdue, noun: 'overdue', emphasis: 'bad' });
    out.push({ scope: 'Yours', value: c.yours.owed, noun: 'owed', href: '#owed' });
    if (c.org.shared > 0) out.push({ scope: 'Shared', value: c.org.shared, noun: 'in 2+ programs' });
    return out;
  }
  if (c.scope === 'programs') {
    for (const p of c.per_program) {
      if (p.open === null) continue;
      const card = cards.find((x) => x.profile_id === p.program_id);
      const title = card ? programTitle(card.program, card.report_type) : 'Program';
      out.push({ scope: title, value: p.open, noun: 'open' });
    }
    out.push({ scope: 'Yours', value: c.yours.owed, noun: 'owed', href: '#owed' });
    const overdue = c.per_program.reduce((n, p) => n + (p.overdue ?? 0), 0);
    if (overdue > 0) out.push({ scope: 'Your programs', value: overdue, noun: 'overdue', emphasis: 'bad' });
    return out;
  }
  out.push({ scope: 'Yours', value: c.yours.owed, noun: 'owed', href: '#owed' });
  if (c.yours.overdue > 0) out.push({ scope: 'Yours', value: c.yours.overdue, noun: 'overdue', emphasis: 'bad' });
  return out;
}

const RANK: Record<VerdictStatus, number> = { on_track: 0, at_risk: 1, off_track: 2 };

/**
 * Today's status. R3-S1: the server's ONE verdict for the viewer's scope
 * (`view.verdict` — the same one /me/counts and /programs return); the
 * client never recomputes it. The fallback only covers an older server.
 */
export function todayVerdict(view: TodayView, cards: readonly ProgramCard[]): VerdictStatus {
  if (view.verdict) return view.verdict.status;
  const base: VerdictStatus = cards.length
    ? cards.reduce<VerdictStatus>((w, c) => (RANK[c.verdict.status] > RANK[w] ? c.verdict.status : w), 'on_track')
    : view.counts.yours.overdue > 0 ? 'at_risk' : 'on_track';
  // A projected hard-date miss is never "on track" (U3).
  return base === 'on_track' && willMissOf(view) ? 'at_risk' : base;
}

/** R3-S1: a card's status word — the server's status for the bar it sits next to (same items as its legend). */
export const cardStatus = (c: Pick<ProgramCard, 'verdict'> & { status?: VerdictStatus }): VerdictStatus => c.status ?? c.verdict.status;

/**
 * R3-U7: the line under Today's title, in the SAME scope as the band's
 * verdict (the server's `view.verdict`):
 *   org       → "N of M programs need attention" (every program);
 *   programs  → the same over the viewer's lead programs only (a legal
 *               delegate never reads the org's verdict);
 *   own       → the minimal viewer verdict: what's theirs ("2 overdue · yours"),
 *               never a program count they can't see.
 */
export function scopedReason(
  view: Pick<TodayView, 'verdict' | 'counts'>, cards: readonly ProgramCard[],
  /** PR A F7: scored programs (CMMC) the server verdict doesn't count, judged by their scorecard model. */
  extra: { total: number; bad: number } = { total: 0, bad: 0 },
): string | null {
  const v = view.verdict;
  if (!v) return verdictReason(cards);
  if (v.scope === 'own') {
    const top = v.reasons.find((r) => r.code !== 'will_miss') ?? v.reasons[0];
    if (top) return top.code === 'will_miss' ? top.text : `${top.text} · yours`;
    return v.open ? null : 'Nothing owed by you';
  }
  const ps = v.programs;
  if (!ps.length && !extra.total) return verdictReason(cards);
  const bad = ps.filter((p) => p.status !== 'on_track').length + extra.bad;
  const total = ps.length + extra.total;
  if (v.scope === 'programs') {
    if (total === 1) return bad ? 'Your program needs attention' : 'Your program is on track';
    return bad ? `${bad} of your ${total} programs need attention` : `Your ${total} programs are on track`;
  }
  const noun = `program${total === 1 ? '' : 's'}`;
  return bad ? `${bad} of ${total} ${noun} need${total === 1 ? 's' : ''} attention` : `${total} ${noun} on track`;
}

/** "3 of 3 programs at risk" — the reason the verdict holds. */
export function verdictReason(cards: readonly ProgramCard[]): string | null {
  if (!cards.length) return null;
  const bad = cards.filter((c) => cardStatus(c) !== 'on_track').length;
  return bad ? `${bad} of ${cards.length} program${cards.length === 1 ? '' : 's'} need attention` : `${cards.length} program${cards.length === 1 ? '' : 's'} on track`;
}

/**
 * The card's reason in ONE scope (V98-3 review U4): the server's
 * `headline_reason`, counted over the same items as the card's legend
 * (`headline_scope` = blockers.scope) — never the program verdict's reason
 * next to an own-scope legend. Null when there is nothing to say.
 */
export function cardReason(c: Pick<ProgramCard, 'headline_reason' | 'headline_scope'>): string | null {
  if (!c.headline_reason) return null;
  return c.headline_scope === 'own' ? `${c.headline_reason.text} · yours` : c.headline_reason.text;
}

/** The scope a card's numbers are counted in (its legend, reason and bar). */
export const cardScope = (c: Pick<ProgramCard, 'headline_scope'>): 'Program' | 'Yours' => (c.headline_scope === 'own' ? 'Yours' : 'Program');

// ---- red once + owners ---------------------------------------------------------------------

/**
 * Red appears once per item, on its highest tier: Owed (the viewer's own) →
 * Open by owner (the top item shown for each other owner) → Next 14 days
 * (never red; overdue there is a count that points up).
 */
export function redTier(key: string, overdue: boolean, tiers: { owed: ReadonlySet<string>; owners: ReadonlySet<string> }): 'owed' | 'owners' | null {
  if (!overdue) return null;
  if (tiers.owed.has(key)) return 'owed';
  if (tiers.owners.has(key)) return 'owners';
  return null;
}

export interface OwnerRow {
  user_id: string | null;
  name: string;
  isViewer: boolean;
  open: number;
  overdue: number;
  /** The owner holds an item projected to miss its hard date (U4: that counts as attention). */
  willMiss: { key: string; date: string } | null;
  /** The most urgent item of this owner (a projected miss first, then overdue, then the nearest date). */
  top: BlockerRow;
  /** Every item of this owner is already in the viewer's Owed list. */
  inOwed: boolean;
}

function urgencyOf(a: BlockerRow, b: BlockerRow): number {
  if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
  const ad = whenOf(a), bd = whenOf(b);
  if (ad !== bd) return ad === null ? 1 : bd === null ? -1 : ad < bd ? -1 : 1;
  return a.item_key < b.item_key ? -1 : 1;
}

/**
 * Org-scoped "Open by owner": one row per holder (and Unassigned); owners
 * with nothing overdue and nothing projected to miss fold into `onTrack`.
 * Red once (V98-3 review U4): a row that only repeats the viewer's own Owed
 * list (the viewer, or an owner whose every item is already in Owed) is
 * REMOVED rather than greyed, so every chip left in the table keeps its
 * tone. `misses`: projected hard-date misses (S9).
 */
export function ownersTable(
  rows: readonly BlockerRow[], viewerId: string, owedKeys: ReadonlySet<string>,
  misses: ReadonlyArray<{ key: string; hard_date: string; user_id?: string | null }> = [],
): { attention: OwnerRow[]; onTrack: OwnerRow[] } {
  const by = new Map<string, BlockerRow[]>();
  for (const r of rows) {
    const ids = r.holder_ids.length ? r.holder_ids : [''];
    for (const id of ids) by.set(id, [...(by.get(id) ?? []), r]);
  }
  const out: OwnerRow[] = [...by].map(([id, list]) => {
    const sorted = [...list].sort(urgencyOf);
    const i = list[0]!.holder_ids.indexOf(id);
    const miss = misses.find((m) => list.some((r) => r.item_key === m.key) && (!m.user_id || m.user_id === id)) ?? null;
    return {
      user_id: id || null,
      name: id ? (list[0]!.holders[i] ?? id) : 'Unassigned',
      isViewer: id === viewerId,
      open: list.length,
      overdue: list.filter((r) => r.overdue).length,
      willMiss: miss ? { key: miss.key, date: day(miss.hard_date) } : null,
      // A projected miss outranks internal overdue as the owner's top item.
      top: (miss && list.find((r) => r.item_key === miss.key)) || sorted[0]!,
      inOwed: id === viewerId || list.every((r) => owedKeys.has(r.item_key)),
    };
  }).filter((o) => !o.inOwed);
  const needs = (o: OwnerRow) => o.overdue > 0 || !!o.willMiss || o.user_id === null;
  out.sort((a, b) => Number(needs(b)) - Number(needs(a)) || Number(!!b.willMiss) - Number(!!a.willMiss) || urgencyOf(a.top, b.top) || (a.name < b.name ? -1 : 1));
  const attention = out.filter(needs);
  return { attention, onTrack: out.filter((o) => !attention.includes(o)) };
}

// ---- next 14 days --------------------------------------------------------------------------

export interface DatedItem {
  key: string; title: string; date: string; owners: string[]; inOwed: boolean; overdue: boolean; ref?: { kind: string; id: string };
  /** The date is a hard date (a filing or external deadline) — tagged "Hard" (U14). */
  hard: boolean;
}

/** Items dated in the next 14 days, by day; overdue split out (owners from routing). */
export function next14(items: readonly DatedItem[], todayDay: string): { overdue: DatedItem[]; days: Array<{ date: string; items: DatedItem[] }> } {
  const end = new Date(Date.parse(`${todayDay}T00:00:00Z`) + 13 * DAY).toISOString().slice(0, 10);
  const overdue = items.filter((i) => i.date < todayDay);
  const days = new Map<string, DatedItem[]>();
  for (const i of items) if (i.date >= todayDay && i.date <= end) days.set(i.date, [...(days.get(i.date) ?? []), i]);
  return { overdue, days: [...days].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([date, list]) => ({ date, items: list })) };
}

/**
 * R3-U1: the date Next 14 days files an item under. A hard date (a filing or
 * an external deadline) is the date that cannot move, so it wins over an
 * earlier soft due date and the item carries the Hard tag; otherwise the due
 * date.
 */
export const calendarDate = (r: { due_at: string | null; hard_date: string | null }): { date: string | null; hard: boolean } =>
  r.hard_date ? { date: day(r.hard_date), hard: true } : { date: r.due_at ? day(r.due_at) : null, hard: false };

/** `canonical`: one title per item (U14) — the viewer's Owed title wins over the list's. */
export function datedFromBlockers(rows: readonly BlockerRow[], owedKeys: ReadonlySet<string>, canonical?: ReadonlyMap<string, string>): DatedItem[] {
  return rows.flatMap((r) => {
    const { date, hard } = calendarDate(r);
    return date ? [{
      key: r.item_key, title: canonical?.get(r.item_key) ?? r.title, date, owners: r.holders.length ? r.holders : ['Unassigned'],
      inOwed: owedKeys.has(r.item_key), overdue: r.overdue, ...(r.ref ? { ref: r.ref } : {}), hard,
    }] : [];
  });
}

export function datedFromOwed(rows: readonly OwedRow[]): DatedItem[] {
  return rows.flatMap((r) => {
    const { date, hard } = calendarDate(r);
    return date ? [{ key: r.key, title: r.title, date, owners: ['You'], inOwed: true, overdue: r.overdue, ref: r.ref, hard }] : [];
  });
}

// ---- accessible names (R3-U25) ----------------------------------------------------------

/**
 * "<verb> · <title>" without saying the verb twice: a title that already
 * starts with the action ("Revise and resubmit · AI acceptable use policy")
 * is the name on its own (label-in-name still holds — it starts with the
 * visible label).
 */
export function actionName(label: string, title: string): string {
  return title.toLowerCase().startsWith(label.toLowerCase()) ? title : `${label} · ${title}`;
}

/** A title without a trailing " · vN" when the version is shown next to it ("Information security policy · v5" + v5). */
export function titleWithoutVersion(title: string, version: number | string): string {
  const v = typeof version === 'number' ? `v${version}` : version;
  const m = /\s*·\s*(v\d+)$/i.exec(title);
  return m && m[1]!.toLowerCase() === v.toLowerCase() ? title.slice(0, m.index) : title;
}

/** "Next: <title> · 3 days overdue" (or "· will miss Oct 10" for a projected miss) — the single primary's label. */
export function nextLabel(row: OwedRow, todayDay: string, miss?: WillMiss | null): string {
  if (miss && miss.key === row.key) return `Next: ${row.title} · will miss ${fmtDay(miss.date)}`;
  const chip = dueChip(row, todayDay);
  return `Next: ${row.title} · ${chip.text.charAt(0).toLowerCase()}${chip.text.slice(1)}`;
}
